import { useEffect, useState } from "react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import OutboundCommunicationsWarning from "@/components/OutboundCommunicationsWarning";
import AddressLookup from "@/components/AddressLookup";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  formatPhone, normalizePhone, validatePhone,
  formatHst, normalizeHst, validateHst,
} from "@/lib/fieldFormatters";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Buildings,
  UsersFour,
  Percent,
  Plugs,
  Bell,
  Shield,
  UploadSimple,
  Database,
  AppWindow,
  Heartbeat,
  Info,
  Palette,
  Plus,
  PencilSimple,
  Trash,
  FloppyDisk,
  CheckSquareOffset,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import CustomerImportPanel from "@/components/CustomerImportPanel";
import DeliveryProfileImportPanel from "@/components/DeliveryProfileImportPanel";
import MigrationsPanel from "@/components/MigrationsPanel";
import ApplicationsPanel from "@/components/ApplicationsPanel";
import SystemDiagnosticsPanel from "@/components/SystemDiagnosticsPanel";
import AboutPanel from "@/components/AboutPanel";
import ProvisioningChecklist from "@/components/ProvisioningChecklist";
import TaxPricingSettingsPanel from "@/components/TaxPricingSettingsPanel";
import BrandingPanel from "@/components/BrandingPanel";

const TABS = [
  { key: "company", label: "Company Info", icon: Buildings },
  { key: "branding", label: "Branding", icon: Palette },
  { key: "users", label: "Users & Roles", icon: UsersFour },
  { key: "customer-import", label: "Customer Import", icon: UploadSimple },
  { key: "delivery-profile-import", label: "Delivery Profile Import", icon: UploadSimple },
  { key: "migrations", label: "Data Migrations", icon: Database },
  { key: "applications", label: "Applications", icon: AppWindow },
  { key: "diagnostics", label: "System Diagnostics", icon: Heartbeat },
  { key: "about", label: "About", icon: Info },
  { key: "tax", label: "Tax & Pricing", icon: Percent },
  { key: "integrations", label: "Integrations", icon: Plugs },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "security", label: "Security", icon: Shield },
];

export default function Administration() {
  const { user } = useAuth();
  const [active, setActive] = useState("company");

  // Route-level guard. Administration is Administrator-only. Sidebar hides
  // the link for non-admins, but the URL is reachable via direct navigation
  // or a bookmark — belt & braces defence: redirect non-admins to /dashboard.
  if (user && user.role !== "Administrator") {
    return <Navigate to="/dashboard" replace data-testid="administration-blocked" />;
  }

  return (
    <div data-testid="administration-page">
      <OutboundCommunicationsWarning />
      <PageHeader
        title="Administration"
        description="Configure company information, users, tax rates, and integrations."
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Vertical tabs */}
        <Card className="border-slate-200 p-2 shadow-sm lg:col-span-3">
          <nav className="space-y-1">
            {TABS.map((t) => {
              const Icon = t.icon;
              const selected = active === t.key;
              return (
                <button
                  key={t.key}
                  data-testid={`admin-tab-${t.key}`}
                  onClick={() => setActive(t.key)}
                  className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                    selected
                      ? "bg-charcoal text-ink-inverse"
                      : "text-ink-secondary hover:bg-surface-alt"
                  }`}
                >
                  <Icon size={18} weight={selected ? "bold" : "regular"} />
                  {t.label}
                </button>
              );
            })}
          </nav>
        </Card>

        {/* Panel */}
        <Card className="border-slate-200 p-6 shadow-sm lg:col-span-9">
          {active === "company" && <CompanyPanel />}
          {active === "branding" && <BrandingPanel />}
          {active === "users" && <UsersPanel />}
          {active === "customer-import" && <CustomerImportPanel />}
          {active === "delivery-profile-import" && <DeliveryProfileImportPanel />}
          {active === "migrations" && <MigrationsPanel />}
          {active === "applications" && <ApplicationsPanel />}
          {active === "diagnostics" && <SystemDiagnosticsPanel />}
          {active === "about" && <AboutPanel />}
          {active === "tax" && <TaxPricingSettingsPanel />}
          {active === "integrations" && <IntegrationsPanel />}
          {active === "notifications" && <NotificationsPanel />}
          {active === "security" && <SecurityPanel />}
        </Card>
      </div>
    </div>
  );
}

function SectionHeader({ title, description, embedded }) {
  return (
    <div className={embedded ? "" : "mb-5 border-b border-slate-200 pb-3"}>
      <h2 className="font-display text-lg font-semibold text-slate-900">{title}</h2>
      <p className="mt-0.5 text-sm text-slate-500">{description}</p>
    </div>
  );
}

function CompanyPanel() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // FOC-011 / Data Quality — per-field validation surfaced under the input.
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/admin/settings/company");
        if (alive) setForm(r.data);
      } catch (e) {
        if (alive) toast.error(e?.response?.data?.detail || "Could not load company info");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
    // Clear error on the field being edited — re-validation happens on save.
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));
  };

  // FOC-011 · Address autocomplete → fill five fields at once.
  const applyAddress = (a) => {
    setForm((f) => ({
      ...f,
      street_address: a.street || f.street_address || "",
      city:           a.city || f.city || "",
      province:       a.province || f.province || "",
      postal_code:    normalizePostal(a.postalCode) || f.postal_code || "",
      // Country isn't part of the current schema — quietly ignore it; we
      // can add a Country field in a follow-up if V1.2 needs multi-country
      // billing addresses.
    }));
    setDirty(true);
    setErrors({});
    toast.success("Address applied — review and adjust any field before saving.");
  };

  const validateAll = () => {
    const next = {};
    const p = validatePhone(form.phone);
    if (!p.ok) next.phone = p.message;
    const h = validateHst(form.hst_number);
    if (!h.ok) next.hst_number = h.message;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!form) return;
    if (!validateAll()) {
      toast.error("Please fix the highlighted fields before saving.");
      return;
    }
    setSaving(true);
    try {
      // Send canonical/normalized values so the server never has to
      // re-parse UI-formatted strings.
      const payload = {
        ...form,
        phone: normalizePhone(form.phone),
        hst_number: normalizeHst(form.hst_number),
        postal_code: normalizePostal(form.postal_code),
      };
      const r = await api.put("/admin/settings/company", payload);
      setForm(r.data);
      setDirty(false);
      toast.success("Company info saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save company info");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/settings/company");
      setForm(r.data);
      setDirty(false);
      setErrors({});
      toast.info("Changes discarded");
    } finally {
      setLoading(false);
    }
  };

  if (loading || !form) {
    return (
      <div data-testid="admin-panel-company" className="py-8 text-center text-sm text-slate-500">
        Loading company info…
      </div>
    );
  }

  return (
    <div data-testid="admin-panel-company">
      <SectionHeader
        title="Company Info"
        description="Details shown on invoices and order confirmations. The Company record is a protected singleton — it cannot be deleted, only edited."
      />
      {/* FOC-011 · Address Lookup — visually distinct card so operators
          never confuse the SEARCH field with the Street Address field
          below. Placed between HST and Street Address so it sits
          directly above the fields it fills. */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FieldRow label="Legal Name" full
          value={form.legal_name} onChange={(v) => setField("legal_name", v)}
          testid="company-legal-name" />
        <FieldRow label="HST/GST #" mono
          value={formatHst(form.hst_number)}
          onChange={(v) => setField("hst_number", v)}
          error={errors.hst_number}
          hint="Format: 9 digits, RT, 4 digits (e.g. 733496822 RT 0001)"
          testid="company-hst" />
        <FieldRow label="Default Email" mono
          value={form.email} onChange={(v) => setField("email", v)}
          testid="company-email" />

        <div className="md:col-span-2">
          <div
            className="rounded-lg border-2 border-brand/40 bg-brand/5 p-4"
            data-testid="company-address-lookup-card"
          >
            <div className="mb-2 flex items-baseline justify-between">
              <Label className="block text-xs font-bold uppercase tracking-wider text-brand">
                🔍 Address Lookup
                <span className="ml-2 rounded bg-white px-1.5 py-0.5 text-[9px] font-medium text-slate-600 shadow-sm">Powered by Google</span>
              </Label>
              <span className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
                Auto-fills the 4 fields below
              </span>
            </div>
            <AddressLookup
              testid="company-address-lookup"
              placeholder="Start typing an address — e.g. 71321 London Rd, Exeter…"
              onSelect={applyAddress}
            />
            <p className="mt-1.5 text-[11px] text-slate-600">
              Type at least 3 characters. Selecting a result fills Street, City, Province, and Postal Code — you can still edit any field manually before saving.
            </p>
          </div>
        </div>

        <FieldRow label="Street Address" full
          value={form.street_address} onChange={(v) => setField("street_address", v)}
          testid="company-street" />
        <FieldRow label="City"
          value={form.city} onChange={(v) => setField("city", v)}
          testid="company-city" />
        <FieldRow label="Province"
          value={form.province} onChange={(v) => setField("province", v)}
          testid="company-province" />
        <FieldRow label="Postal Code" mono
          value={formatPostal(form.postal_code)}
          onChange={(v) => setField("postal_code", v)}
          testid="company-postal" />
        <FieldRow label="Phone" mono
          value={formatPhone(form.phone)}
          onChange={(v) => setField("phone", v)}
          error={errors.phone}
          testid="company-phone" />
      </div>
      {form.updated_at && (
        <p className="mt-4 text-xs text-slate-500">
          Last updated {new Date(form.updated_at).toLocaleString("en-CA")}
          {form.updated_by ? ` by ${form.updated_by}` : ""}
        </p>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <Button
          variant="outline"
          className="border-slate-300"
          onClick={handleCancel}
          disabled={!dirty || saving}
          data-testid="admin-company-cancel"
        >
          Cancel
        </Button>
        <Button
          className="bg-[#1A1A1A] hover:bg-[#1A1A1A]"
          onClick={handleSave}
          disabled={!dirty || saving}
          data-testid="admin-company-save"
        >
          <FloppyDisk size={14} weight="bold" className="mr-1" />
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}

const USER_ROLES = ["Administrator", "Owner", "Office Staff", "Driver"];

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | user
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [checklistUser, setChecklistUser] = useState(null); // user to show checklist for

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Removed ${u.full_name}`);
      setDeleteConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete user");
    }
  };

  return (
    <div data-testid="admin-panel-users">
      <div className="mb-5 flex items-start justify-between">
        <SectionHeader
          title="Users & Roles"
          description="Manage staff accounts and permissions."
          embedded
        />
        <Button
          data-testid="users-new-btn"
          onClick={() => setEditing("new")}
          className="bg-[#1A1A1A] hover:bg-[#1A1A1A]"
        >
          <Plus size={14} weight="bold" className="mr-1" /> New User
        </Button>
      </div>

      {loading && (
        <div className="py-8 text-center text-sm text-slate-500">Loading users…</div>
      )}

      {!loading && (
        <div className="overflow-hidden rounded-md border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2.5 font-semibold">Name</th>
                <th className="px-4 py-2.5 font-semibold">Email</th>
                <th className="px-4 py-2.5 font-semibold">Role</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold w-24 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-slate-100" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{u.full_name}</td>
                  <td className="px-4 py-3 font-mono text-[13px] text-[#1A1A1A]">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        u.status === "Active"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {u.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      data-testid={`user-checklist-${u.email}`}
                      onClick={() => setChecklistUser(u)}
                      className="mr-1 rounded-md p-1.5 text-slate-600 hover:bg-slate-100"
                      title="Provisioning Checklist"
                    >
                      <CheckSquareOffset size={14} weight="bold" />
                    </button>
                    <button
                      data-testid={`user-edit-${u.email}`}
                      onClick={() => setEditing(u)}
                      className="mr-1 rounded-md p-1.5 text-slate-600 hover:bg-slate-100"
                      title="Edit"
                    >
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button
                      data-testid={`user-delete-${u.email}`}
                      onClick={() => setDeleteConfirm(u)}
                      className="rounded-md p-1.5 text-slate-600 hover:bg-red-50 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">
                    No users found. Click &quot;New User&quot; to add one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <UserEditor
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {checklistUser && (
        <Dialog open onOpenChange={(o) => !o && setChecklistUser(null)}>
          <DialogContent data-testid="provisioning-checklist-dialog" className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Provisioning Checklist · {checklistUser.full_name}</DialogTitle>
              <DialogDescription className="font-mono text-[11px]">
                {checklistUser.email}
              </DialogDescription>
            </DialogHeader>
            <ProvisioningChecklist userId={checklistUser.id} />
          </DialogContent>
        </Dialog>
      )}

      {deleteConfirm && (
        <Dialog open onOpenChange={(o) => !o && setDeleteConfirm(null)}>
          <DialogContent data-testid="user-delete-confirm">
            <DialogHeader>
              <DialogTitle>Remove {deleteConfirm.full_name}?</DialogTitle>
              <DialogDescription>
                This removes the user account. Their existing tasks stay assigned to
                their email, but they will no longer be able to sign in.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <Button
                data-testid="user-delete-confirm-btn"
                onClick={() => handleDelete(deleteConfirm)}
                className="bg-red-600 hover:bg-red-700"
              >
                Remove
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function UserEditor({ initial, onClose, onSaved }) {
  const isNew = !initial;
  const [form, setForm] = useState(() => ({
    full_name: initial?.full_name || "",
    email: initial?.email || "",
    role: initial?.role || "Office Staff",
    status: initial?.status || "Active",
  }));
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/users", { ...form, email: form.email.trim().toLowerCase() });
        toast.success("User added");
      } else {
        // Email is immutable on the backend UserUpdate model — send only mutable fields.
        await api.put(`/users/${initial.id}`, {
          full_name: form.full_name,
          role: form.role,
          status: form.status,
        });
        toast.success("User updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="user-editor" className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isNew ? "Add User" : `Edit ${initial.full_name}`}</DialogTitle>
          <DialogDescription>
            Roles govern which pages, widgets, and actions this user can access.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Full Name<span className="text-red-500"> *</span>
            </Label>
            <Input
              data-testid="user-editor-name"
              value={form.full_name}
              onChange={(e) => setField("full_name", e.target.value)}
              placeholder="Nathan King"
            />
          </div>

          <div>
            <Label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Email<span className="text-red-500"> *</span>
            </Label>
            <Input
              data-testid="user-editor-email"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              disabled={!isNew}
              placeholder="nathan@coolmanfuels.ca"
              className="font-mono"
            />
            {!isNew && (
              <p className="mt-1 text-xs text-slate-400">
                Email cannot be changed — delete and recreate if the address needs to change.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Role
              </Label>
              <Select value={form.role} onValueChange={(v) => setField("role", v)}>
                <SelectTrigger data-testid="user-editor-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {USER_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Status
              </Label>
              <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger data-testid="user-editor-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            data-testid="user-editor-save"
            onClick={handleSave}
            disabled={saving}
            className="bg-[#1A1A1A] hover:bg-[#1A1A1A]"
          >
            <FloppyDisk size={14} weight="bold" className="mr-1" />
            {saving ? "Saving…" : isNew ? "Add" : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TaxPanel() {
  return (
    <div data-testid="admin-panel-tax">
      <SectionHeader title="Tax Rates" description="Configure provincial taxes applied at checkout." />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <FieldRow label="HST (Ontario)" placeholder="13.0" mono suffix="%" />
        <FieldRow label="Federal Excise (Diesel)" placeholder="0.04" mono suffix="$/L" />
        <FieldRow label="Federal Excise (Gasoline)" placeholder="0.10" mono suffix="$/L" />
        <FieldRow label="Carbon Levy" placeholder="0.1431" mono suffix="$/L" />
      </div>
    </div>
  );
}

function IntegrationsPanel() {
  const integrations = [
    { name: "ERP System (Customer Sync)", status: "Connected", note: "Weekly sync · last run Mon 06:00" },
    { name: "Email Provider", status: "Connected", note: "Used for driver notifications" },
    { name: "SMS Provider", status: "Not Connected", note: "Optional driver SMS alerts" },
  ];
  return (
    <div data-testid="admin-panel-integrations">
      <SectionHeader title="Integrations" description="External services connected to Fuel Order Centre." />
      <div className="space-y-2">
        {integrations.map((i) => (
          <div
            key={i.name}
            className="flex items-center justify-between rounded-md border border-slate-200 p-4"
          >
            <div>
              <div className="font-medium text-slate-900">{i.name}</div>
              <div className="text-xs text-slate-500">{i.note}</div>
            </div>
            <span
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
                i.status === "Connected"
                  ? "border-green-200 bg-green-50 text-green-700"
                  : "border-slate-200 bg-slate-50 text-slate-600"
              }`}
            >
              {i.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function NotificationsPanel() {
  return (
    <div data-testid="admin-panel-notifications">
      <SectionHeader title="Notifications" description="Choose which events trigger emails to drivers and staff." />
      <div className="space-y-3">
        <ToggleRow label="Email driver when order is assigned" defaultOn />
        <ToggleRow label="Email driver when order is rescheduled" defaultOn />
        <ToggleRow label="Email customer with delivery confirmation" />
        <ToggleRow label="Daily summary email to dispatch" defaultOn />
      </div>
    </div>
  );
}

function SecurityPanel() {
  return (
    <div data-testid="admin-panel-security">
      <SectionHeader title="Security" description="Sign-in policies and session management." />
      <div className="space-y-3">
        <ToggleRow label="Require 2-factor authentication for administrators" />
        <ToggleRow label="Auto sign-out after 30 minutes of inactivity" defaultOn />
        <ToggleRow label="Log all customer record views for audit" defaultOn />
      </div>
    </div>
  );
}

function FieldRow({ label, placeholder, full, mono, suffix, value, onChange, testid, error, hint }) {
  const controlled = value !== undefined && onChange !== undefined;
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </Label>
      <div className="relative">
        <Input
          data-testid={testid}
          placeholder={placeholder}
          className={`${mono ? "font-mono" : ""} ${suffix ? "pr-10" : ""} ${error ? "border-red-400 focus-visible:ring-red-300" : ""}`}
          {...(controlled ? { value: value ?? "", onChange: (e) => onChange(e.target.value) } : {})}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-500">
            {suffix}
          </span>
        )}
      </div>
      {error ? (
        <p className="mt-1 text-[11px] font-medium text-red-600" data-testid={`${testid}-error`}>{error}</p>
      ) : hint ? (
        <p className="mt-1 text-[11px] text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

// Canadian postal-code formatter — "A1A1A1" ⇄ "A1A 1A1". Non-alnum is stripped.
function normalizePostal(raw) {
  if (!raw) return "";
  return String(raw).replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 6);
}
function formatPostal(raw) {
  const s = normalizePostal(raw);
  if (s.length <= 3) return s;
  return `${s.slice(0, 3)} ${s.slice(3)}`;
}

function ToggleRow({ label, defaultOn }) {
  const [on, setOn] = useState(!!defaultOn);
  return (
    <div className="flex items-center justify-between rounded-md border border-slate-200 p-4">
      <div className="text-sm text-slate-800">{label}</div>
      <Switch checked={on} onCheckedChange={setOn} />
    </div>
  );
}
